/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { LeftOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'

export interface FullScreenHeaderProps {
  title: string
  onBack: () => void
  /** Rendered right after the title on the same line (e.g. an info icon). */
  titleAdornment?: React.ReactNode
  /** Right-aligned header actions — `HeaderActionButton`s. */
  actions?: React.ReactNode
}

/**
 * The header of a `FullScreenOverlay` page: BACK link + large title on the
 * left, actions on the right, a divider underneath.
 */
const FullScreenHeader: React.FC<FullScreenHeaderProps> = ({
  title,
  onBack,
  titleAdornment,
  actions,
}) => (
  <div css={headerContainerStyle}>
    <div css={leftSideStyle}>
      <button type='button' css={backLinkStyle} onClick={onBack}>
        <LeftOutlined aria-hidden='true' />
        <Text
          variant='captionXSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          style='margin: 0; letter-spacing: 1.44px;'
        >
          BACK
        </Text>
      </button>
      <div css={titleRowStyle}>
        <Text variant='displaySmall' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(title)}
        </Text>
        {titleAdornment}
      </div>
    </div>
    {actions ? <div css={rightSideStyle}>{actions}</div> : null}
  </div>
)

export default FullScreenHeader

// ---- styled ----
// Wraps rather than squeezes: on a narrow window the right side drops below the
// title instead of crushing a long title onto several lines.
const headerContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
  margin-bottom: 24px;
`

const leftSideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
`

const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const rightSideStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`
